import { fetchAPI } from 'api';
import { KYCData, EidProviderName, Countries } from 'types';

export interface MFACodeResponse {
  mfaCode: string;
}

export interface SignInSuccessData {
  firstOnboardingCompleted: boolean;
  loginCountry: string;
  kyc: KYCData;
  requestConsent: boolean;
  tokenTtlInSeconds: number;
  token: string;
  userId: string;
}

export interface EidProviderInfo {
  url: string;
  autoStartToken?: string;
  providerName: EidProviderName;
}

export const initiatePasswordLogin = async (
  deviceId: string,
  email: string,
  password: string,
  rememberMe: boolean,
  receiveOrderPreviewId?: string
) => {
  return fetchAPI<
    {
      mfaToken?: string;
      methods?: { type: string; channels: string[]; phoneNumber: string }[];
    } & SignInSuccessData
  >('auth', {
    method: 'POST',
    body: {
      grantType: 'PASSWORD',
      deviceInstallId: deviceId,
      email,
      password,
      rememberMe,
      receiveOrderPreviewId,
    },
  });
};

export const initiateEIDLogin = async ({
  deviceId,
  nationality,
  userId,
  redirectUrl,
  rememberMe,
  providerName,
  useHeadless,
  receiveOrderPreviewId,
}: {
  deviceId: string;
  nationality: string;
  userId?: string;
  redirectUrl?: string;
  rememberMe: boolean;
  providerName?: EidProviderName;
  useHeadless?: boolean;
  receiveOrderPreviewId?: string;
}) => {
  const { eidToken: token, providerInfo } = await fetchAPI<{
    eidToken: string;
    providerInfo: EidProviderInfo;
  }>(`auth`, {
    method: 'POST',
    body: {
      grantType: 'EID',
      deviceInstallId: deviceId,
      provider: {
        providerName,
        flow: useHeadless ? 'headless' : 'redirect',
      },
      nationality,
      userId,
      redirectUrl,
      rememberMe,
      receiveOrderPreviewId,
    },
  });
  return { token, providerInfo };
};

export const loginStatus = async (
  eidToken: string
): Promise<SignInSuccessData> => {
  return fetchAPI<SignInSuccessData>(`eid/completion`, {
    method: 'POST',
    body: {
      eidToken,
    },
  });
};

export type ScriveProvider =
  | 'seBankID'
  | 'noBankID'
  | 'fiTupas'
  | 'dkMitID'
  | 'nlIDIN'
  | 'onfido';

export interface ScriveStartResponse {
  transactionId: string;
  accessUrl: string;
}

export const startScriveEid = async ({
  provider,
  redirectUrl,
  deviceId,
  rememberMe,
  nationality,
  receiveOrderPreviewId,
}: {
  provider: ScriveProvider;
  redirectUrl: string;
  deviceId: string;
  rememberMe: boolean;
  nationality: string;
  receiveOrderPreviewId?: string;
}) => {
  return fetchAPI<ScriveStartResponse>('auth', {
    method: 'POST',
    body: {
      grantType: 'EID_TRANSACTION_START',
      deviceInstallId: deviceId,
      provider,
      redirectUrl,
      nationality,
      rememberMe,
      receiveOrderPreviewId,
    },
  });
};

export const completeScriveEid = async ({
  transactionId,
  deviceId,
  rememberMe,
  nationality,
  receiveOrderPreviewId,
}: {
  transactionId: string;
  deviceId: string;
  rememberMe: boolean;
  nationality: string;
  receiveOrderPreviewId?: string;
}) => {
  return fetchAPI<SignInSuccessData>('auth', {
    method: 'POST',
    body: {
      grantType: 'EID_TRANSACTION_COMPLETE',
      deviceInstallId: deviceId,
      transactionId,
      nationality,
      rememberMe,
      receiveOrderPreviewId,
    },
  });
};

export const getAppCountries = (onramp?: boolean) => {
  if (onramp) {
    return fetchAPI<Countries>('countries?onramp=true');
  }
  return fetchAPI<Countries>('countries');
};

export const initiateMFA = (mfaToken: string) => {
  return fetchAPI<MFACodeResponse | undefined>('mfa/challenge/phone', {
    method: 'POST',
    body: {
      channel: 'SMS',
      mfaToken,
    },
  });
};

export const completeMFA = (
  code: string,
  mfaToken: string,
  deviceId: string
) => {
  return fetchAPI<SignInSuccessData>('mfa/challenge-completion', {
    method: 'POST',
    body: {
      type: 'PHONE',
      deviceInstallId: deviceId,
      code,
      mfaToken,
    },
  });
};

export const resetPassword = (emailAddress: string) => {
  return fetchAPI('auth/password/reset', {
    method: 'POST',
    body: {
      emailAddress,
    },
  });
};
